import React, { Component } from "react";
import { Field, Form, FormikProps, withFormik } from "formik";
import * as Yup from "yup";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import ListItemText from "@material-ui/core/ListItemText";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

import ActionFormButton from "../../../common/ActionFormButton";
import {
  getRowItemObject,
  renderRowCheckboxes,
  renderRowWithSingleItem,
  renderRowWithTwoItems,
  renderSectionTitle,
  Row,
  RowItem,
  Section,
  Wrapper,
} from "../../../common/FormUtils";
import {
  Community,
  CommunityConfig,
  CommunityDailyCheckIn,
  CommunityFeatures,
  CommunityTab,
} from "../../../../types/Community";
// Namespaced (v8) SDK: FieldValue.delete() is how this screen clears a config
// key rather than storing a default value. See handleSubmit.
import firebase from "../../../../database/firebase";
import { Author } from "../../../../types/Author";
import { normalizeAiGuidelines } from "../../../../utils/aiGuidelines";
import getAllAuthors from "../../../../database/authors/getAllAuthors";
import ImageUpload from "../../../common/ImageUpload";
import Input from "../../../common/CustomInput";
import WhatsAppGroupInviteSettings from "./WhatsAppGroupInviteSettings";
import CoachReportRoomsSettings from "./CoachReportRoomsSettings";
import CommunityPersonalizationPanel from "./CommunityPersonalizationPanel";
import {
  buildPersonalizationFields,
  HEX_COLOR_PATTERN,
  PersonalizationDraft,
  readPersonalizationDraft,
} from "./communityPersonalization";
import {
  buildSubRequiredPatch,
  SUB_REQUIRED_SEEDED_DEFAULT,
} from "./subRequiredPatch";

type ScoreSourceType =
  | "learning_plan_step_completed"
  | "habit_completed"
  | "session_logged"
  | "task_completed"
  | "streak_days"
  | "mood_score"
  | "steps"
  | "active_calories"
  | "sleep_hours";

type ScoreMetricForm = {
  id: string;
  label: string;
  description: string;
  weight: number;
  sources: {
    type: ScoreSourceType;
    target: number;
    weight: number;
  }[];
};

const SCORE_SOURCE_OPTIONS: Array<{
  value: ScoreSourceType;
  label: string;
  defaultTarget: number;
}> = [
  {
    value: "learning_plan_step_completed",
    label: "Learning-plan steps completed",
    defaultTarget: 1,
  },
  { value: "habit_completed", label: "Habits completed", defaultTarget: 3 },
  { value: "session_logged", label: "Sessions logged", defaultTarget: 1 },
  { value: "task_completed", label: "Tasks completed", defaultTarget: 3 },
  { value: "streak_days", label: "Current streak days", defaultTarget: 7 },
  { value: "mood_score", label: "Logged mood score", defaultTarget: 5 },
  { value: "steps", label: "Health steps", defaultTarget: 8000 },
  {
    value: "active_calories",
    label: "Health active calories",
    defaultTarget: 400,
  },
  { value: "sleep_hours", label: "Health sleep hours", defaultTarget: 8 },
];

// Coach-facing app controls. Order here is the order of the toggles in the
// "Features and layout" section. A community that has never been touched has
// every feature on, which is the platform's behavior today.
const FEATURE_OPTIONS: Array<{
  key: keyof CommunityFeatures;
  label: string;
  hint: string;
}> = [
  {
    key: "journaling",
    label: "Journaling",
    hint: "Reflection chat, guided reflection, weekly review and quick log",
  },
  {
    key: "streaks",
    label: "Streaks",
    hint: "Streak pill and streak celebrations",
  },
  { key: "levels", label: "Levels", hint: "Mastery levels and XP chips" },
  {
    key: "leaderboard",
    label: "Leaderboard",
    hint: "Member leaderboard and member directory",
  },
  {
    key: "memberBrain",
    label: "Member brain",
    hint: "Behavioral distillation and the profile memory card",
  },
  { key: "aiChat", label: "AI chat", hint: "In-app AI coach chat" },
  { key: "voiceAgent", label: "Voice agent", hint: "Live voice call" },
  { key: "shop", label: "Shop", hint: "In-app shop and product surfaces" },
  {
    key: "community",
    label: "Community feed",
    hint: "Member feed and posting",
  },
];

const HOME_SECTION_KEYS = [
  "checkIn",
  "tools",
  "todayTasks",
  "cyclePlanner",
  "habits",
  "winback",
  "weeklyReview",
  "streak",
];

// Daily check-in slots and what follows a submit. The question templates are
// deliberately not editable here: they are structured question objects and the
// MCP validates them before writing. This section shapes the behavior around
// them only, and never sends a templates key, so a coach's questions survive
// every save from this screen.
const CHECK_IN_SLOT_OPTIONS: Array<{ key: string; label: string }> = [
  { key: "morning", label: "Morning" },
  { key: "evening", label: "Evening" },
];

const CHECK_IN_OUTCOME_OPTIONS: Array<{
  value: string;
  label: string;
  hint: string;
}> = [
  {
    value: "planner",
    label: "Daily planner",
    hint: "Opens the planner after the submit",
  },
  {
    value: "insight",
    label: "Daily insight",
    hint: "Shows the insight and the score",
  },
  {
    value: "none",
    label: "Nothing",
    hint: "Short confirmation, back to the home screen",
  },
];

const booleanToTriState = (value?: boolean) =>
  value === true ? "on" : value === false ? "off" : "";

type FormValues = {
  id: string;
  title: string;
  description: string;
  shortDescription: string;
  coverURL: string;
  logoURL: string;
  categoryName: string;
  categoryId: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  surfaceColor: string;
  surfaceElevatedColor: string;
  surfaceBorderColor: string;
  dividerColor: string;
  textColor: string;
  secondaryTextColor: string;
  mutedTextColor: string;
  onAccentColor: string;
  typographyScale: number | "";
  radiusScale: number | "";
  tags: string;
  authorName: string;
  authorId: string;
  voiceId: string;
  agentId: string;
  profilePhotoURL: string;
  authorDescription: string;
  authorAiGuidelines: string;
  imagesURL: string;
  status: string;
  createdAt: string;
  showAlways: boolean;
  subRequired: boolean;
  whatsappPaywallFeedbackEnabled: boolean;
  whatsappCoachEnabled: boolean;
  writingStyle: string;
  visualStyle: string;
  stylePack: string;
  fontTheme: string;
  tabs: CommunityTab[];
  associatedCommunityIds: string[];
  slug: string;
  iosAppStoreURL: string;
  androidPlayStoreURL: string;
  listInShowcase: boolean;
  waitlistMode: boolean;
  metaPixelId: string;
  tiktokPixelId: string;
  googleTagId: string;
  scoreEnabled: boolean;
  scoreName: string;
  scoreMetrics: ScoreMetricForm[];
  features: Record<string, boolean>;
  checkInStorage: string;
  chatHistory: string;
  homeSections: string;
  enforcePrivacy: boolean;
  communityIdentity: string;
  checkInEnabled: boolean;
  checkInName: string;
  checkInSlots: string[];
  // Tri-state on purpose: "" means the key stays absent so the app's own
  // default applies. "on" and "off" are explicit coach choices.
  checkInScoring: string;
  checkInRewards: string;
  checkInAiFallback: string;
  checkInOutcomeMorning: string;
  checkInOutcomeEvening: string;
};

type OuterProps = {
  onChageFormToEditMode: () => void;
  onRemoveItem: () => void;
  onCreateItem: (community: Community) => void | Promise<void>;
  onEditItem: (community: Community) => void | Promise<void>;
  mode: string;
  item: Community;
  communities?: Community[];
  communityId?: string;
  isActive?: boolean;
};

type Props = FormikProps<FormValues> & OuterProps;

// Picks the community's main author from its Author collection. Lives in a
// function component so it can use the live getAllAuthors hook; the class form
// stays a class. Selecting an author writes the canonical community.authorId and
// (via onSelect) keeps the embedded community.author snapshot in sync.
const MainAuthorPicker = ({
  communityId,
  value,
  disabled,
  onSelect,
}: {
  communityId: string;
  value: string;
  disabled: boolean;
  onSelect: (author: Author) => void;
}) => {
  const authors = getAllAuthors({ communityId });
  const selected = authors.find((a) => a.id === value);

  return (
    <>
      <Row>
        <RowItem>
          <FormControl fullWidth>
            <InputLabel id="main-author-label" shrink>
              Main author
            </InputLabel>
            <Select
              labelId="main-author-label"
              displayEmpty
              disabled={disabled || !communityId}
              value={selected ? value : ""}
              onChange={(event) => {
                const author = authors.find((a) => a.id === event.target.value);
                if (author) onSelect(author);
              }}
              renderValue={(selectedId) => {
                const author = authors.find((a) => a.id === selectedId);
                return (
                  author?.fullName || "Select the community's main author"
                );
              }}
            >
              {authors.length ? (
                authors.map((author) => (
                  <MenuItem key={author.id} value={author.id}>
                    <ListItemText
                      primary={author.fullName || author.id}
                      secondary={
                        [
                          author.voiceId ? "voice" : null,
                          author.agentId ? "agent" : null,
                          author.knowledgeVectorStoreId ? "knowledge" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ") || undefined
                      }
                    />
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  {communityId
                    ? "No authors yet. Create one on the Authors screen."
                    : "Save the community first."}
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </RowItem>
      </Row>
      {selected && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 12,
          }}
        >
          {selected.profilePhotoURL ? (
            <img
              src={selected.profilePhotoURL}
              alt={selected.fullName}
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : null}
          <div style={{ fontSize: 13, color: "#666" }}>
            AI chat, voice and the knowledge base resolve through{" "}
            <strong>{selected.fullName}</strong>.
          </div>
        </div>
      )}
    </>
  );
};

class CommunityForm extends Component<Props> {
  renderBasicInfoSection = () => {
    const titleInput = getRowItemObject(
      "Title",
      "Type the community title",
      "text",
      "title"
    );
    const communityIdInput = {
      ...getRowItemObject(
        "Community ID",
        "Read-only community identifier",
        "text",
        "id"
      ),
      readOnly: true,
    };
    const shortDescriptionInput = getRowItemObject(
      "Short Description",
      "Write a short description",
      "text",
      "shortDescription"
    );
    const descriptionInput = getRowItemObject(
      "Description",
      "Write a detailed description",
      "textarea",
      "description"
    );

    return (
      <Section>
        {renderSectionTitle("Basic settings")}
        {renderRowWithTwoItems(titleInput, shortDescriptionInput, this.props)}
        {renderRowWithSingleItem(communityIdInput, this.props)}
        {renderRowWithSingleItem(descriptionInput, this.props)}
      </Section>
    );
  };

  renderAuthorSection = () => {
    const { values, setFieldValue, mode } = this.props;
    const communityId = values.id || this.props.communityId || "";

    return (
      <Section>
        {renderSectionTitle("Main author")}
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          Pick the community's canonical coach from its authors. The choice is
          saved as the community's main author, and its details (name, photo,
          voice, agent and AI guidelines) are mirrored onto the community so
          older app builds keep working. Edit author details on the Authors
          screen.
        </div>
        <MainAuthorPicker
          communityId={communityId}
          value={values.authorId}
          disabled={mode === "detail"}
          onSelect={(author) => {
            setFieldValue("authorId", author.id);
            setFieldValue("authorName", author.fullName || "");
            setFieldValue("voiceId", author.voiceId || "");
            setFieldValue("agentId", author.agentId || "");
            setFieldValue("profilePhotoURL", author.profilePhotoURL || "");
            setFieldValue("authorDescription", author.description || "");
            setFieldValue(
              "authorAiGuidelines",
              normalizeAiGuidelines(author.aiGuidelines)
            );
          }}
        />
      </Section>
    );
  };

  renderConfigSection = () => {
    const showAlwaysInput = getRowItemObject(
      "Show Always",
      "Always show this community",
      "checkbox",
      "showAlways"
    );
    const subRequiredInput = getRowItemObject(
      "Sub Required",
      "Subscription required",
      "checkbox",
      "subRequired"
    );
    const whatsappFeedbackInput = getRowItemObject(
      "WhatsApp Paywall Feedback",
      "Offer the optional WhatsApp follow-up only when a healthy sender and approved templates are also configured",
      "checkbox",
      "whatsappPaywallFeedbackEnabled"
    );
    const whatsappCoachInput = getRowItemObject(
      "WhatsApp Coach",
      "Allow the server-side coach agent to answer opted-in members inside the active 24-hour service window",
      "checkbox",
      "whatsappCoachEnabled"
    );

    return (
      <Section>
        {renderSectionTitle("Subscription")}
        {renderRowCheckboxes(showAlwaysInput, subRequiredInput, this.props)}
        {renderRowWithSingleItem(whatsappFeedbackInput, this.props)}
        {renderRowWithSingleItem(whatsappCoachInput, this.props)}
        <WhatsAppGroupInviteSettings
          communityId={this.props.values.id || this.props.communityId || ""}
          disabled={this.props.mode === "detail"}
        />
        <CoachReportRoomsSettings
          communityId={this.props.values.id || this.props.communityId || ""}
          disabled={this.props.mode === "detail"}
        />
      </Section>
    );
  };

  renderAssociatedCommunitiesSection = () => {
    const { communities = [], mode, setFieldValue, values } = this.props;
    const selectedCommunityIds = values.associatedCommunityIds || [];
    const currentCommunityId = values.id || this.props.communityId || "";
    const isDisabled = mode === "detail";
    const selectableCommunities = communities
      .filter((community) => community.id && community.id !== currentCommunityId)
      .sort((a, b) => (a.title || a.id).localeCompare(b.title || b.id));

    const getCommunityLabel = (communityId: string) => {
      const community = communities.find((item) => item.id === communityId);
      return community?.title || communityId;
    };

    return (
      <Section>
        {renderSectionTitle("Switchable communities")}
        <Row>
          <RowItem>
            <FormControl fullWidth>
              <InputLabel id="associated-communities-label">
                Communities users can switch to
              </InputLabel>
              <Select
                labelId="associated-communities-label"
                multiple
                disabled={isDisabled}
                value={selectedCommunityIds}
                onChange={(event) => {
                  const value = event.target.value;
                  setFieldValue(
                    "associatedCommunityIds",
                    Array.isArray(value) ? value : String(value).split(",")
                  );
                }}
                renderValue={(selected) =>
                  (selected as string[]).map(getCommunityLabel).join(", ")
                }
              >
                {selectableCommunities.length ? (
                  selectableCommunities.map((community) => (
                    <MenuItem key={community.id} value={community.id}>
                      <Checkbox
                        checked={selectedCommunityIds.indexOf(community.id) > -1}
                      />
                      <ListItemText primary={community.title || community.id} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No other communities available</MenuItem>
                )}
              </Select>
            </FormControl>
          </RowItem>
        </Row>
      </Section>
    );
  };

  renderAIStylesSection = () => {
    const writingStyleInput = getRowItemObject(
      "Writing Style",
      "e.g. bold & provocative, gentle & poetic, humorous...",
      "text",
      "writingStyle"
    );
    const visualStyleInput = getRowItemObject(
      "Visual Style",
      "e.g. minimalist, cinematic, watercolor...",
      "text",
      "visualStyle"
    );

    return (
      <Section>
        {renderSectionTitle("AI Styles")}
        {renderRowWithTwoItems(writingStyleInput, visualStyleInput, this.props)}
      </Section>
    );
  };

  renderPersonalizationSection = () => {
    const {
      values,
      setFieldValue,
      handleChange,
      handleBlur,
      errors,
      touched,
      mode,
      item,
    } = this.props;
    const personalizationValues: PersonalizationDraft = {
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      accentColor: values.accentColor,
      backgroundColor: values.backgroundColor,
      surfaceColor: values.surfaceColor,
      surfaceElevatedColor: values.surfaceElevatedColor,
      surfaceBorderColor: values.surfaceBorderColor,
      dividerColor: values.dividerColor,
      textColor: values.textColor,
      secondaryTextColor: values.secondaryTextColor,
      mutedTextColor: values.mutedTextColor,
      onAccentColor: values.onAccentColor,
      fontTheme: values.fontTheme,
      stylePack: values.stylePack,
      typographyScale: values.typographyScale,
      radiusScale: values.radiusScale,
    };

    return (
      <Section>
        {renderSectionTitle("App personalization")}
        <CommunityPersonalizationPanel
          values={personalizationValues}
          savedValues={readPersonalizationDraft(item)}
          errors={errors as Partial<Record<keyof PersonalizationDraft, string>>}
          touched={
            touched as Partial<Record<keyof PersonalizationDraft, boolean>>
          }
          mode={mode}
          isThemeBasePinned={values.id === "nicolehope124"}
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          handleBlur={handleBlur}
        />
      </Section>
    );
  };

  renderFeaturesAndLayoutSection = () => {
    const { values, setFieldValue, handleChange, mode } = this.props;
    const isDisabled = mode === "detail";
    const features = values.features || {};

    return (
      <Section>
        {renderSectionTitle("Features and layout")}
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          Shape the app around the coach's method. Every switch is on unless
          turned off, so leaving this section alone keeps today's behavior. The
          MCP writes the same keys with{" "}
          <code>update_community {"{ config: { ... } }"}</code>.
        </div>
        <Row>
          {FEATURE_OPTIONS.map((option) => (
            <RowItem key={option.key as string}>
              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  fontSize: 14,
                  color: "#666",
                }}
                title={option.hint}
              >
                <input
                  type="checkbox"
                  checked={features[option.key as string] !== false}
                  disabled={isDisabled}
                  onChange={(event) =>
                    setFieldValue("features", {
                      ...features,
                      [option.key as string]: event.target.checked,
                    })
                  }
                />
                <span>
                  {option.label}
                  <span style={{ display: "block", fontSize: 12, color: "#999" }}>
                    {option.hint}
                  </span>
                </span>
              </label>
            </RowItem>
          ))}
        </Row>
        <Row>
          <RowItem>
            <FormControl fullWidth>
              <InputLabel id="check-in-storage-label" shrink>
                Check-in storage
              </InputLabel>
              <Select
                labelId="check-in-storage-label"
                displayEmpty
                disabled={isDisabled}
                value={values.checkInStorage || ""}
                onChange={(event) =>
                  setFieldValue("checkInStorage", String(event.target.value))
                }
              >
                <MenuItem value="">
                  <ListItemText
                    primary="Cloud (default)"
                    secondary="Answers are saved, so trends and history work"
                  />
                </MenuItem>
                <MenuItem value="ephemeral">
                  <ListItemText
                    primary="Ephemeral"
                    secondary="Kept on the device for the day, never written"
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </RowItem>
          <RowItem>
            <FormControl fullWidth>
              <InputLabel id="chat-history-label" shrink>
                Chat history
              </InputLabel>
              <Select
                labelId="chat-history-label"
                displayEmpty
                disabled={isDisabled}
                value={values.chatHistory || ""}
                onChange={(event) =>
                  setFieldValue("chatHistory", String(event.target.value))
                }
              >
                <MenuItem value="">
                  <ListItemText
                    primary="Cloud (default)"
                    secondary="Transcripts are stored, member can return to them"
                  />
                </MenuItem>
                <MenuItem value="none">
                  <ListItemText
                    primary="None"
                    secondary="Memory only, history is gone on restart"
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </RowItem>
        </Row>
        <Row>
          <RowItem>
            <Input
              label="Home sections (ordered, comma separated)"
              name="homeSections"
              value={values.homeSections}
              placeholder="tools, checkIn, habits"
              disabled={isDisabled}
              onChange={handleChange}
            />
            <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>
              The order you type is the order on the home screen, and a section
              you leave out is not shown. Leave empty for the built-in order.
              Valid keys: {HOME_SECTION_KEYS.join(", ")}.
            </div>
          </RowItem>
        </Row>
        <Row>
          <RowItem>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                fontSize: 14,
                color: "#666",
              }}
            >
              <input
                type="checkbox"
                name="enforcePrivacy"
                checked={values.enforcePrivacy}
                disabled={isDisabled}
                onChange={handleChange}
              />
              Enforce privacy
            </label>
            <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>
              Preset for a coach who promises the app does not record or profile
              the member. It resolves chat history to none, check-in storage to
              ephemeral, member brain off and identity to display name, unless
              you set that control explicitly here, in which case your choice
              wins.
            </div>
          </RowItem>
          <RowItem>
            <FormControl fullWidth>
              <InputLabel id="community-identity-label" shrink>
                Member identity
              </InputLabel>
              <Select
                labelId="community-identity-label"
                displayEmpty
                disabled={isDisabled}
                value={values.communityIdentity || ""}
                onChange={(event) =>
                  setFieldValue("communityIdentity", String(event.target.value))
                }
              >
                <MenuItem value="">
                  <ListItemText
                    primary="Full name (default)"
                    secondary="The name from signup shows to other members"
                  />
                </MenuItem>
                <MenuItem value="displayName">
                  <ListItemText
                    primary="Display name"
                    secondary="Short chosen name, family name stays private"
                  />
                </MenuItem>
              </Select>
            </FormControl>
          </RowItem>
        </Row>
      </Section>
    );
  };

  renderCheckInSection = () => {
    const { values, setFieldValue, handleChange, mode } = this.props;
    const isDisabled = mode === "detail";
    const slots = values.checkInSlots || [];

    const toggleSlot = (key: string, checked: boolean) => {
      const next = checked
        ? [...slots.filter((slot) => slot !== key), key]
        : slots.filter((slot) => slot !== key);
      // Keep the stored order stable so the app reads morning before evening.
      setFieldValue(
        "checkInSlots",
        CHECK_IN_SLOT_OPTIONS.map((option) => option.key).filter((key2) =>
          next.includes(key2)
        )
      );
    };

    const renderTriState = (
      field: "checkInScoring" | "checkInRewards" | "checkInAiFallback",
      labelId: string,
      label: string,
      defaultLabel: string,
      defaultHint: string,
      onHint: string,
      offHint: string
    ) => (
      <RowItem>
        <FormControl fullWidth>
          <InputLabel id={labelId} shrink>
            {label}
          </InputLabel>
          <Select
            labelId={labelId}
            displayEmpty
            disabled={isDisabled}
            value={values[field] || ""}
            onChange={(event) =>
              setFieldValue(field, String(event.target.value))
            }
          >
            <MenuItem value="">
              <ListItemText primary={defaultLabel} secondary={defaultHint} />
            </MenuItem>
            <MenuItem value="on">
              <ListItemText primary="On" secondary={onHint} />
            </MenuItem>
            <MenuItem value="off">
              <ListItemText primary="Off" secondary={offHint} />
            </MenuItem>
          </Select>
        </FormControl>
      </RowItem>
    );

    const renderOutcome = (
      field: "checkInOutcomeMorning" | "checkInOutcomeEvening",
      labelId: string,
      label: string,
      defaultHint: string
    ) => (
      <RowItem>
        <FormControl fullWidth>
          <InputLabel id={labelId} shrink>
            {label}
          </InputLabel>
          <Select
            labelId={labelId}
            displayEmpty
            disabled={isDisabled}
            value={values[field] || ""}
            onChange={(event) =>
              setFieldValue(field, String(event.target.value))
            }
          >
            <MenuItem value="">
              <ListItemText primary="Default" secondary={defaultHint} />
            </MenuItem>
            {CHECK_IN_OUTCOME_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText
                  primary={option.label}
                  secondary={option.hint}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </RowItem>
    );

    return (
      <Section>
        {renderSectionTitle("Check-in")}
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          How the daily check-in behaves. Every control left on its default
          keeps today's behavior. The questions themselves are edited
          through the MCP with{" "}
          <code>{"update_community { dailyCheckIn: { templates: { ... } } }"}</code>
          ; this screen never touches them.
        </div>
        <Row>
          <RowItem>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                fontSize: 14,
                color: "#666",
              }}
            >
              <input
                type="checkbox"
                name="checkInEnabled"
                checked={values.checkInEnabled}
                disabled={isDisabled}
                onChange={handleChange}
              />
              Use the coach's own check-in
            </label>
            <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>
              Off means the app uses its built-in template. Turn it on once the
              questions have been written through the MCP.
            </div>
          </RowItem>
          <RowItem>
            <Input
              label="Check-in name"
              name="checkInName"
              value={values.checkInName}
              placeholder="Return Daily"
              disabled={isDisabled}
              onChange={handleChange}
            />
          </RowItem>
        </Row>
        <Row>
          <RowItem>
            <div style={{ fontSize: 14, color: "#666", marginBottom: 6 }}>
              Slots
            </div>
            <div style={{ display: "flex", gap: 18 }}>
              {CHECK_IN_SLOT_OPTIONS.map((option) => (
                <label
                  key={option.key}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    fontSize: 14,
                    color: "#666",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={slots.includes(option.key)}
                    disabled={isDisabled}
                    onChange={(event) =>
                      toggleSlot(option.key, event.target.checked)
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#999", marginTop: 6 }}>
              Both, or neither, is the default: the clock picks morning or
              evening. Tick exactly one and the check-in loses its time of day,
              is available any time, and always uses that slot's questions.
            </div>
          </RowItem>
        </Row>
        <Row>
          {renderTriState(
            "checkInScoring",
            "check-in-scoring-label",
            "Scoring",
            "Default",
            "Follows the score calculator setting",
            "Computes a score and the daily insight",
            "No score, no insight call, no progress ring"
          )}
          {renderTriState(
            "checkInRewards",
            "check-in-rewards-label",
            "Rewards",
            "Default (on)",
            "Submitting pays XP and advances the streak",
            "Submitting pays XP and advances the streak",
            "No XP and no streak for the check-in"
          )}
          {renderTriState(
            "checkInAiFallback",
            "check-in-ai-fallback-label",
            "AI fallback",
            "Default (on)",
            "The app may write its own questions when none are set",
            "The app may write its own questions when none are set",
            "Only the coach's questions, or the built-in bank"
          )}
        </Row>
        <Row>
          {renderOutcome(
            "checkInOutcomeMorning",
            "check-in-outcome-morning-label",
            "After the morning check-in",
            "Daily planner"
          )}
          {renderOutcome(
            "checkInOutcomeEvening",
            "check-in-outcome-evening-label",
            "After the evening check-in",
            "Daily insight"
          )}
        </Row>
      </Section>
    );
  };

  renderScoreCalculatorSection = () => {
    const { values, handleChange, setFieldValue, mode, errors } = this.props;
    const isDisabled = mode === "detail";
    const metrics = values.scoreMetrics || [];

    const replaceMetric = (index: number, patch: Partial<ScoreMetricForm>) => {
      setFieldValue(
        "scoreMetrics",
        metrics.map((metric, metricIndex) =>
          metricIndex === index ? { ...metric, ...patch } : metric
        )
      );
    };

    const replaceSource = (
      metricIndex: number,
      sourceIndex: number,
      patch: Partial<ScoreMetricForm["sources"][number]>
    ) => {
      const metric = metrics[metricIndex];
      replaceMetric(metricIndex, {
        sources: metric.sources.map((source, index) =>
          index === sourceIndex ? { ...source, ...patch } : source
        ),
      });
    };

    return (
      <Section>
        {renderSectionTitle("Daily score calculator")}
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          Optional. When enabled, logged learning steps, habits, sessions,
          tasks, streaks and selected health signals update the member's daily
          score. Add as many community-specific metrics as the program needs.
          Screen time and app opens are never scored. The MCP can configure the
          same model with{" "}
          <code>configure_score_calculator</code>.
        </div>
        <Row>
          <RowItem>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                fontSize: 14,
                color: "#666",
              }}
            >
              <input
                type="checkbox"
                name="scoreEnabled"
                checked={values.scoreEnabled}
                disabled={isDisabled}
                onChange={handleChange}
              />
              Enable automatic logged-activity scoring
            </label>
          </RowItem>
        </Row>
        <Row>
          <RowItem>
            <Input
              label="Score name"
              name="scoreName"
              value={values.scoreName}
              placeholder="Momentum Score"
              disabled={isDisabled}
              onChange={handleChange}
              error={
                typeof errors.scoreName === "string"
                  ? errors.scoreName
                  : undefined
              }
            />
          </RowItem>
        </Row>
        {metrics.map((metric, metricIndex) => (
          <div
            key={`${metric.id}-${metricIndex}`}
            style={{
              marginTop: 16,
              padding: 16,
              border: "1px solid #e5e5e5",
              borderRadius: 8,
            }}
          >
            <Row>
              <RowItem>
                <Input
                  label={`Metric ${metricIndex + 1} id`}
                  value={metric.id}
                  placeholder="learning"
                  disabled={isDisabled}
                  onChange={(event) =>
                    replaceMetric(metricIndex, { id: event.target.value })
                  }
                />
              </RowItem>
              <RowItem>
                <Input
                  label="Member-facing label"
                  value={metric.label}
                  placeholder="Learning"
                  disabled={isDisabled}
                  onChange={(event) =>
                    replaceMetric(metricIndex, { label: event.target.value })
                  }
                />
              </RowItem>
            </Row>
            <Row>
              <RowItem>
                <Input
                  label="Explanation"
                  value={metric.description}
                  placeholder="Progress through the learning program"
                  disabled={isDisabled}
                  onChange={(event) =>
                    replaceMetric(metricIndex, {
                      description: event.target.value,
                    })
                  }
                />
              </RowItem>
              <RowItem>
                <Input
                  label="Metric weight"
                  type="number"
                  min={0.01}
                  step={0.1}
                  value={metric.weight}
                  disabled={isDisabled}
                  onChange={(event) =>
                    replaceMetric(metricIndex, {
                      weight: Number(event.target.value),
                    })
                  }
                />
              </RowItem>
            </Row>

            {metric.sources.map((source, sourceIndex) => (
              <Row key={`${source.type}-${sourceIndex}`}>
                <RowItem>
                  <FormControl fullWidth>
                    <InputLabel shrink>Logged source</InputLabel>
                    <Select
                      displayEmpty
                      disabled={isDisabled}
                      value={source.type}
                      onChange={(event) => {
                        const type = event.target.value as ScoreSourceType;
                        const option = SCORE_SOURCE_OPTIONS.find(
                          (item) => item.value === type
                        );
                        replaceSource(metricIndex, sourceIndex, {
                          type,
                          target: option?.defaultTarget || source.target,
                        });
                      }}
                    >
                      {SCORE_SOURCE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </RowItem>
                <RowItem>
                  <Input
                    label="Daily target"
                    type="number"
                    min={0.01}
                    step={0.1}
                    value={source.target}
                    disabled={isDisabled}
                    onChange={(event) =>
                      replaceSource(metricIndex, sourceIndex, {
                        target: Number(event.target.value),
                      })
                    }
                  />
                </RowItem>
                <RowItem>
                  <Input
                    label="Source weight"
                    type="number"
                    min={0.01}
                    step={0.1}
                    value={source.weight}
                    disabled={isDisabled}
                    onChange={(event) =>
                      replaceSource(metricIndex, sourceIndex, {
                        weight: Number(event.target.value),
                      })
                    }
                  />
                </RowItem>
                {!isDisabled ? (
                  <RowItem>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() =>
                        replaceMetric(metricIndex, {
                          sources: metric.sources.filter(
                            (_, index) => index !== sourceIndex
                          ),
                        })
                      }
                    >
                      Remove source
                    </Button>
                  </RowItem>
                ) : null}
              </Row>
            ))}

            {!isDisabled ? (
              <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() =>
                    replaceMetric(metricIndex, {
                      sources: [
                        ...metric.sources,
                        {
                          type: "habit_completed",
                          target: 3,
                          weight: 1,
                        },
                      ],
                    })
                  }
                >
                  + Add source
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() =>
                    setFieldValue(
                      "scoreMetrics",
                      metrics.filter((_, index) => index !== metricIndex)
                    )
                  }
                >
                  Remove metric
                </Button>
              </div>
            ) : null}
          </div>
        ))}
        {!isDisabled ? (
          <Button
            variant="outlined"
            color="primary"
            style={{ marginTop: 16 }}
            onClick={() =>
              setFieldValue("scoreMetrics", [
                ...metrics,
                {
                  id: `metric_${metrics.length + 1}`,
                  label: "",
                  description: "",
                  weight: 1,
                  sources: [
                    {
                      type: "habit_completed",
                      target: 3,
                      weight: 1,
                    },
                  ],
                },
              ])
            }
          >
            + Add metric
          </Button>
        ) : null}
      </Section>
    );
  };

  renderTabsSection = () => {
    const { values, setFieldValue, mode } = this.props;
    const tabs: CommunityTab[] = values.tabs || [];
    const isDisabled = mode === "detail";

    const updateTab = (index: number, field: keyof CommunityTab, value: string) => {
      const updated = tabs.map((tab, i) => (i === index ? { ...tab, [field]: value } : tab));
      setFieldValue("tabs", updated);
    };

    const addTab = () => {
      setFieldValue("tabs", [...tabs, { key: "", icon: "", route: "", label: "", i18nKey: "" }]);
    };

    const removeTab = (index: number) => {
      setFieldValue("tabs", tabs.filter((_, i) => i !== index));
    };

    return (
      <Section>
        {renderSectionTitle("Tabs")}
        {tabs.map((tab, index) => (
          <div key={index} style={{ marginBottom: 16, padding: 12, border: "1px solid #eee", borderRadius: 4 }}>
            <Row>
              <RowItem>
                <Input label="Key" name={`tabs.${index}.key`} value={tab.key} disabled={isDisabled} onChange={(e) => updateTab(index, "key", e.target.value)} />
              </RowItem>
              <RowItem>
                <Input label="Icon" name={`tabs.${index}.icon`} value={tab.icon} disabled={isDisabled} onChange={(e) => updateTab(index, "icon", e.target.value)} />
              </RowItem>
            </Row>
            <Row>
              <RowItem>
                <Input label="Route" name={`tabs.${index}.route`} value={tab.route} disabled={isDisabled} onChange={(e) => updateTab(index, "route", e.target.value)} />
              </RowItem>
              <RowItem>
                <Input label="Label" name={`tabs.${index}.label`} value={tab.label || ""} disabled={isDisabled} onChange={(e) => updateTab(index, "label", e.target.value)} />
              </RowItem>
            </Row>
            <Row>
              <RowItem>
                <Input label="i18n Key" name={`tabs.${index}.i18nKey`} value={tab.i18nKey || ""} disabled={isDisabled} onChange={(e) => updateTab(index, "i18nKey", e.target.value)} />
              </RowItem>
              <RowItem>
                {!isDisabled && (
                  <Button variant="outlined" color="secondary" onClick={() => removeTab(index)} style={{ marginTop: 16 }}>
                    Remove Tab
                  </Button>
                )}
              </RowItem>
            </Row>
          </div>
        ))}
        {!isDisabled && (
          <Button variant="outlined" color="primary" onClick={addTab}>
            + Add Tab
          </Button>
        )}
      </Section>
    );
  };

  renderLandingSection = () => {
    const slugInput = getRowItemObject(
      "Landing page slug",
      "Vanity URL path, e.g. maria-coaching (defaults to the community id)",
      "text",
      "slug"
    );
    const iosURLInput = getRowItemObject(
      "iOS App Store URL",
      "https://apps.apple.com/app/...",
      "text",
      "iosAppStoreURL"
    );
    const androidURLInput = getRowItemObject(
      "Android Play Store URL",
      "https://play.google.com/store/apps/details?id=...",
      "text",
      "androidPlayStoreURL"
    );

    const { values, handleChange, mode } = this.props;

    return (
      <Section>
        {renderSectionTitle("Public landing page")}
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          Settings for this coach's standalone web landing page. The slug is the
          public URL path; leave it empty to use the community id. Store URLs
          power the page's download buttons. Generate the landing copy itself
          from the Landing tab.
        </div>
        {renderRowWithSingleItem(slugInput, this.props)}
        {renderRowWithTwoItems(iosURLInput, androidURLInput, this.props)}
        <Row>
          <RowItem>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                fontSize: 14,
                color: "#666",
                marginTop: 8,
              }}
            >
              <input
                type="checkbox"
                name="listInShowcase"
                checked={!!values.listInShowcase}
                disabled={mode === "detail"}
                onChange={handleChange}
              />
              Feature this community in the public tribed.io/showcase gallery
              (opt-in)
            </label>
          </RowItem>
        </Row>
        <Row>
          <RowItem>
            <label
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                fontSize: 14,
                color: "#666",
                marginTop: 8,
              }}
            >
              <input
                type="checkbox"
                name="waitlistMode"
                checked={!!values.waitlistMode}
                disabled={mode === "detail"}
                onChange={handleChange}
              />
              Waitlist mode (app still being built): show a "join the waitlist"
              email capture instead of the buy/onboarding button
            </label>
          </RowItem>
        </Row>
      </Section>
    );
  };

  renderTrackingSection = () => {
    const metaInput = getRowItemObject(
      "Meta Pixel ID",
      "Coach's own Meta pixel, e.g. 1044327481583302 (optional)",
      "text",
      "metaPixelId"
    );
    const tiktokInput = getRowItemObject(
      "TikTok Pixel ID",
      "Coach's own TikTok pixel id (optional)",
      "text",
      "tiktokPixelId"
    );
    const googleInput = getRowItemObject(
      "Google Tag ID",
      "GA4 (G-...) and/or Google Ads (AW-...), comma separated (optional)",
      "text",
      "googleTagId"
    );

    return (
      <Section>
        {renderSectionTitle("Tracking pixels (optional)")}
        <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
          Optional. If this coach wants to build their OWN ad audiences, paste
          their pixel ids here. They fire on the coach's landing page alongside
          Tribed's shared pixel, so the coach owns their retargeting and
          lookalike audiences. Leave blank to use only Tribed's shared pixel.
        </div>
        {renderRowWithTwoItems(metaInput, tiktokInput, this.props)}
        {renderRowWithSingleItem(googleInput, this.props)}
      </Section>
    );
  };

  renderMetaSection = () => {
    const categoryNameInput = getRowItemObject(
      "Category name",
      "Type the category name",
      "text",
      "categoryName"
    );
    const categoryIdInput = getRowItemObject(
      "Category id",
      "Type the category id",
      "text",
      "categoryId"
    );
    const coverURLInput = getRowItemObject(
      "Cover URL",
      "Add a cover image URL",
      "text",
      "coverURL"
    );
    const logoURLInput = getRowItemObject(
      "Logo URL",
      "Add a logo image URL (used as watermark)",
      "text",
      "logoURL"
    );
    const tagsInput = getRowItemObject(
      "Tags",
      "Comma separated tags",
      "text",
      "tags"
    );
    const imagesInput = getRowItemObject(
      "Images URLs",
      "Comma separated image URLs",
      "text",
      "imagesURL"
    );
    const statusInput = getRowItemObject(
      "Status",
      "active / inactive",
      "text",
      "status"
    );

    return (
      <Section>
        {renderSectionTitle("Meta")}
        {renderRowWithTwoItems(categoryNameInput, categoryIdInput, this.props)}
        {renderRowWithSingleItem(coverURLInput, this.props)}
        {renderRowWithSingleItem(logoURLInput, this.props)}
        {renderRowWithSingleItem(tagsInput, this.props)}
        {renderRowWithSingleItem(imagesInput, this.props)}
        {renderRowWithSingleItem(statusInput, this.props)}
        <div style={{ fontSize: 13, color: "#666", marginBottom: 4 }}>
          Cover image (large hero shown across the community)
        </div>
        <Field name="coverURL">
          {({ field, form }) => (
            <ImageUpload
              field={field}
              form={form}
              actionId={this.props.values?.id}
              reference={"CommunityCover"}
            />
          )}
        </Field>
        <div style={{ fontSize: 13, color: "#666", margin: "12px 0 4px" }}>
          Logo (small mark used as watermark)
        </div>
        <Field name="logoURL">
          {({ field, form }) => (
            <ImageUpload
              field={field}
              form={form}
              actionId={this.props.values?.id}
              reference={"CommunityLogo"}
            />
          )}
        </Field>
      </Section>
    );
  };

  render() {
    const { onChageFormToEditMode, onRemoveItem, isSubmitting, mode, isActive } =
      this.props;

    return (
      <Wrapper>
        <Form>
          {this.renderBasicInfoSection()}
          {this.renderAuthorSection()}
          {this.renderConfigSection()}
          {this.renderAssociatedCommunitiesSection()}
          {this.renderAIStylesSection()}
          {this.renderPersonalizationSection()}
          {this.renderFeaturesAndLayoutSection()}
          {this.renderCheckInSection()}
          {this.renderScoreCalculatorSection()}
          {this.renderTabsSection()}
          {this.renderMetaSection()}
          {this.renderLandingSection()}
          {this.renderTrackingSection()}
          <ActionFormButton
            onChageFormToEditMode={onChageFormToEditMode}
            onClick={() => {
              console.log("Handling Submit");
              this.props.handleSubmit();
            }}
            onRemoveItem={onRemoveItem}
            disabled={isSubmitting}
            entity="Community"
            canBeRemoved
            mode={mode}
            hidden={!isActive}
          />
        </Form>
      </Wrapper>
    );
  }
}

const optionalHexColor = Yup.string().test(
  "hex-color",
  "Use a full six-digit hex color such as #1A2B3C",
  (value) => !value || HEX_COLOR_PATTERN.test(value.trim())
);

const optionalScale = (min: number, max: number) =>
  Yup.number()
    .nullable()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .min(min, `Use a value from ${min} to ${max}`)
    .max(max, `Use a value from ${min} to ${max}`);

export const validationSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  authorName: Yup.string().required("Author name is required"),

  primaryColor: optionalHexColor,
  secondaryColor: optionalHexColor,
  accentColor: optionalHexColor,
  backgroundColor: optionalHexColor,
  surfaceColor: optionalHexColor,
  surfaceElevatedColor: optionalHexColor,
  surfaceBorderColor: optionalHexColor,
  dividerColor: optionalHexColor,
  textColor: optionalHexColor,
  secondaryTextColor: optionalHexColor,
  mutedTextColor: optionalHexColor,
  onAccentColor: optionalHexColor,
  typographyScale: optionalScale(0.85, 1.25),
  radiusScale: optionalScale(0, 2),

  status: Yup.mixed().oneOf(["active", "inactive"]),
  scoreName: Yup.string().test(
    "complete-score-calculator",
    "Enable scoring only with a name ending in “Score” and at least one complete metric with a logged source and positive targets/weights.",
    function (value) {
      const values = this.parent as FormValues;
      if (!values.scoreEnabled) return true;
      const metrics = values.scoreMetrics || [];
      const ids = metrics.map((metric) => metric.id.trim());
      return (
        /\bscore$/i.test(String(value || "").trim()) &&
        metrics.length > 0 &&
        new Set(ids).size === ids.length &&
        metrics.every(
          (metric) =>
            Boolean(metric.id.trim()) &&
            /^[a-z0-9][a-z0-9_-]*$/.test(metric.id.trim()) &&
            Boolean(metric.label.trim()) &&
            Boolean(metric.description.trim()) &&
            metric.weight > 0 &&
            metric.sources.length > 0 &&
            metric.sources.every(
              (source) => source.target > 0 && source.weight > 0
            )
        )
      );
    }
  ),
});

const enhance = withFormik<OuterProps, FormValues>({
  enableReinitialize: true,
  mapPropsToValues: ({ item, communityId }: OuterProps): FormValues => ({
    id: item?.id || communityId || "",
    title: item?.title || "",
    description: item?.description || "",
    shortDescription: item?.shortDescription || "",
    coverURL: item?.coverURL || "",
    logoURL: item?.logoURL || "",
    categoryName: item?.categoryName || "",
    categoryId: item?.categoryId || "",
    ...readPersonalizationDraft(item),
    tags: item?.tags?.join(", ") || "",
    authorName: item?.author?.fullName || "",
    authorId: item?.author?.id || "",
    voiceId: item?.author?.voiceId || "",
    agentId: item?.author?.agentId || "",
    profilePhotoURL: item?.author?.profilePhotoURL || "",
    authorDescription: item?.author?.description || "",
    authorAiGuidelines: normalizeAiGuidelines(item?.author?.aiGuidelines),
    imagesURL: item?.imagesURL?.join(", ") || "",
    status: item?.status || "active",
    createdAt: item?.createdAt || "",
    showAlways: item?.config?.showAlways ?? true,
    subRequired: item?.config?.subRequired ?? SUB_REQUIRED_SEEDED_DEFAULT,
    whatsappPaywallFeedbackEnabled:
      item?.config?.whatsappPaywallFeedbackEnabled ?? false,
    whatsappCoachEnabled: item?.config?.whatsappCoachEnabled ?? false,
    writingStyle: item?.writingStyle || "",
    visualStyle: item?.visualStyle || "",
    tabs: item?.tabs || [],
    associatedCommunityIds: item?.associatedCommunityIds || [],
    slug: item?.slug || "",
    iosAppStoreURL: item?.iosAppStoreURL || "",
    androidPlayStoreURL: item?.androidPlayStoreURL || "",
    listInShowcase: item?.listInShowcase ?? false,
    waitlistMode: item?.waitlistMode ?? false,
    metaPixelId: item?.metaPixelId || "",
    tiktokPixelId: item?.tiktokPixelId || "",
    googleTagId: item?.googleTagId || "",
    features: { ...(item?.config?.features || {}) },
    checkInStorage: item?.config?.checkInStorage || "",
    chatHistory: item?.config?.chatHistory || "",
    homeSections: (item?.config?.homeSections || []).join(", "),
    enforcePrivacy: item?.config?.enforcePrivacy ?? false,
    communityIdentity: item?.config?.communityIdentity || "",
    checkInEnabled: item?.dailyCheckIn?.enabled ?? false,
    checkInName: item?.dailyCheckIn?.name || "",
    checkInSlots: item?.dailyCheckIn?.slots || [],
    checkInScoring: booleanToTriState(item?.dailyCheckIn?.scoring),
    checkInRewards: booleanToTriState(item?.dailyCheckIn?.rewards),
    checkInAiFallback: booleanToTriState(item?.dailyCheckIn?.aiFallback),
    checkInOutcomeMorning: item?.dailyCheckIn?.outcome?.morning || "",
    checkInOutcomeEvening: item?.dailyCheckIn?.outcome?.evening || "",
    scoreEnabled: item?.insightScore?.enabled === true,
    scoreName: item?.insightScore?.scoreName || "",
    scoreMetrics: (item?.insightScore?.metrics || []).map((metric) => ({
      id: metric.id || "",
      label: metric.label || "",
      description: metric.description || "",
      weight: metric.weight || 1,
      sources: (metric.sources || []).map((source) => ({
        type: source.type,
        target: source.target,
        weight: source.weight || 1,
      })),
    })),
  }),
  validationSchema,
  handleSubmit(values, { setSubmitting, props }) {
    console.log("Submitting Community form with values:", values);

    const { onCreateItem, onEditItem, mode } = props;
    const properCallback = mode === "edit" ? onEditItem : onCreateItem;

    const tagsArray =
      values.tags
        ?.split(",")
        .map((tag) => tag.trim())
        .filter(Boolean) || [];

    const imagesArray =
      values.imagesURL
        ?.split(",")
        .map((url) => url.trim())
        .filter(Boolean) || [];

    // The community write is set(merge: true), which already deep-merges the
    // nested config map, so this form only sends the keys it owns. Spreading
    // the loaded item.config back would rewrite keys this screen does not
    // manage (blockAI, the WhatsApp and Stripe settings) from possibly stale
    // form state and clobber a concurrent MCP write.
    //
    // Absent is not the same as default here. The app's enforcePrivacy preset
    // only resolves a granular key that is ABSENT, so storing "cloud" because
    // a picker sits at its default would make enforcePrivacy a permanent no-op
    // for this community, including for later MCP writes. A control at its
    // default therefore DELETES the key. On create the write is a plain set(),
    // which rejects a delete sentinel and has nothing to clear, so the key is
    // omitted instead.
    const isEditingExisting = mode === "edit";
    const clearedOnEdit = (key: string) =>
      isEditingExisting
        ? { [key]: firebase.firestore.FieldValue.delete() }
        : {};
    const setOrClear = (key: string, value: string) =>
      value ? { [key]: value } : clearedOnEdit(key);

    const homeSections = (values.homeSections || "")
      .split(",")
      .map((section) => section.trim())
      .filter((section) => HOME_SECTION_KEYS.includes(section));

    // Same absent-is-not-default rule as the config block above, and the same
    // create-mode exception. Slots ticked both or neither is the built-in
    // "clock picks the slot" behavior, so only exactly one slot is written.
    // A templates key is never sent, so the coach's questions are untouched.
    const triStateOrClear = (key: string, value: string) =>
      value === "on"
        ? { [key]: true }
        : value === "off"
        ? { [key]: false }
        : clearedOnEdit(key);

    const checkInSlots = CHECK_IN_SLOT_OPTIONS.map((option) => option.key)
      .filter((slot) => (values.checkInSlots || []).includes(slot));

    const checkInOutcome = {
      ...setOrClear("morning", values.checkInOutcomeMorning),
      ...setOrClear("evening", values.checkInOutcomeEvening),
    };

    const dailyCheckIn = {
      ...(values.checkInEnabled
        ? { enabled: true }
        : clearedOnEdit("enabled")),
      ...setOrClear("name", values.checkInName.trim()),
      ...(checkInSlots.length === 1
        ? { slots: checkInSlots }
        : clearedOnEdit("slots")),
      ...triStateOrClear("scoring", values.checkInScoring),
      ...triStateOrClear("rewards", values.checkInRewards),
      ...triStateOrClear("aiFallback", values.checkInAiFallback),
      ...(Object.keys(checkInOutcome).length
        ? { outcome: checkInOutcome }
        : {}),
    };

    const scoreName = values.scoreName.trim();
    const scoreMetrics = values.scoreMetrics.map((metric) => ({
      id: metric.id.trim(),
      label: metric.label.trim(),
      description: metric.description.trim(),
      weight: Number(metric.weight) || 1,
      sources: metric.sources.map((source) => ({
        type: source.type,
        target: Number(source.target),
        weight: Number(source.weight) || 1,
      })),
    }));
    const scoreCalculatorComplete =
      values.scoreEnabled &&
      /\bscore$/i.test(scoreName) &&
      scoreMetrics.length > 0 &&
      scoreMetrics.every(
        (metric) =>
          Boolean(metric.id) &&
          /^[a-z0-9][a-z0-9_-]*$/.test(metric.id) &&
          Boolean(metric.label) &&
          Boolean(metric.description) &&
          metric.weight > 0 &&
          metric.sources.length > 0 &&
          metric.sources.every(
            (source) => source.target > 0 && source.weight > 0
          )
      );
    const existingScore = props.item?.insightScore;
    const scoreCalculatorUnchanged =
      existingScore?.enabled === values.scoreEnabled &&
      existingScore?.scoreVersion === 1 &&
      existingScore.scoreName === scoreName &&
      JSON.stringify(existingScore.metrics) === JSON.stringify(scoreMetrics);
    const insightScore = scoreCalculatorComplete
      ? {
          enabled: true,
          scoreName,
          metrics: scoreMetrics,
          scoreVersion: 1,
          _configuredAt: scoreCalculatorUnchanged
            ? existingScore?._configuredAt || new Date().toISOString()
            : new Date().toISOString(),
        }
      : existingScore
        ? { ...existingScore, enabled: false }
        : undefined;
    const personalization = buildPersonalizationFields({
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      accentColor: values.accentColor,
      backgroundColor: values.backgroundColor,
      surfaceColor: values.surfaceColor,
      surfaceElevatedColor: values.surfaceElevatedColor,
      surfaceBorderColor: values.surfaceBorderColor,
      dividerColor: values.dividerColor,
      textColor: values.textColor,
      secondaryTextColor: values.secondaryTextColor,
      mutedTextColor: values.mutedTextColor,
      onAccentColor: values.onAccentColor,
      fontTheme: values.fontTheme,
      stylePack: values.stylePack,
      typographyScale: values.typographyScale,
      radiusScale: values.radiusScale,
    });

    const community: Community = {
      id: values.id,
      title: values.title,
      description: values.description,
      shortDescription: values.shortDescription,
      coverURL: values.coverURL,
      logoURL: values.logoURL,
      categoryName: values.categoryName,
      categoryId: values.categoryId,
      ...personalization,
      tags: tagsArray,
      // Canonical pointer: resolveCommunityAuthor() matches on this first.
      authorId: values.authorId || "",
      // Denormalized snapshot kept in sync from the picked author for older
      // app builds and the live voice config preference.
      author: {
        id: values.authorId,
        fullName: values.authorName,
        voiceId: values.voiceId,
        agentId: values.agentId,
        profilePhotoURL: values.profilePhotoURL,
        description: values.authorDescription || "",
        aiGuidelines: values.authorAiGuidelines || "",
      },
      imagesURL: imagesArray,
      createdAt: values.createdAt || new Date().toISOString(),
      config: {
        showAlways: values.showAlways,
        // Omitted entirely when it was absent and untouched. The write is
        // set(merge: true), so an omitted key leaves the stored document alone.
        ...buildSubRequiredPatch(
          props.item?.config?.subRequired as boolean | undefined,
          values.subRequired,
          isEditingExisting
        ),
        whatsappPaywallFeedbackEnabled:
          values.whatsappPaywallFeedbackEnabled,
        whatsappCoachEnabled: values.whatsappCoachEnabled,
        // Only the switches the operator actually touched, plus whatever was
        // already stored. Writing all nine every save would pin memberBrain
        // true and defeat enforcePrivacy for good.
        ...(Object.keys(values.features || {}).length
          ? { features: values.features }
          : {}),
        enforcePrivacy: values.enforcePrivacy ?? false,
        ...setOrClear("checkInStorage", values.checkInStorage),
        ...setOrClear("chatHistory", values.chatHistory),
        ...setOrClear("communityIdentity", values.communityIdentity),
        // An empty list means "built-in order", and merge ignores an omitted
        // key, so the stored array has to be deleted rather than left behind.
        ...(homeSections.length
          ? { homeSections }
          : clearedOnEdit("homeSections")),
      } as unknown as CommunityConfig,
      // On create every control at its default yields an empty map, and an
      // empty dailyCheckIn map says nothing, so it is omitted entirely.
      ...(Object.keys(dailyCheckIn).length
        ? { dailyCheckIn: dailyCheckIn as unknown as CommunityDailyCheckIn }
        : {}),
      writingStyle: values.writingStyle || "",
      visualStyle: values.visualStyle || "",
      tabs: values.tabs || [],
      associatedCommunityIds: values.associatedCommunityIds || [],
      slug: values.slug?.trim().toLowerCase() || "",
      iosAppStoreURL: values.iosAppStoreURL?.trim() || "",
      androidPlayStoreURL: values.androidPlayStoreURL?.trim() || "",
      listInShowcase: values.listInShowcase ?? false,
      waitlistMode: values.waitlistMode ?? false,
      metaPixelId: values.metaPixelId?.trim() || "",
      tiktokPixelId: values.tiktokPixelId?.trim() || "",
      googleTagId: values.googleTagId?.trim() || "",
      ...(insightScore ? { insightScore } : {}),
      //@ts-ignore
      status: values.status || "active",
    };

    properCallback(community);
    setSubmitting(false);
  },
  //@ts-ignore
})(CommunityForm);

export default enhance;
